import { Badge, type BadgeProps } from '@components/ui/badge';
import type { FC } from 'react';

/**
 * Any of the codegen'd classification-level enums (study, campaign, etc.) shares
 * the same four members. Typed as a string union so callers don't need to cast
 * between equivalent enum copies.
 */
export type ClassificationValue = 'UNCLASSIFIED' | 'CUI' | 'SECRET' | 'TOP_SECRET';

type ClassificationBadgeProps = {
  classification: ClassificationValue;
  className?: string;
  size?: BadgeProps['size'];
};

const VARIANT: Record<ClassificationValue, BadgeProps['variant']> = {
  UNCLASSIFIED: 'capco-unclassified',
  CUI: 'capco-cui',
  SECRET: 'capco-secret',
  TOP_SECRET: 'capco-top-secret',
};

const LABEL: Record<ClassificationValue, string> = {
  UNCLASSIFIED: 'Unclassified',
  CUI: 'CUI',
  SECRET: 'Secret',
  TOP_SECRET: 'Top Secret',
};

/**
 * Standardized classification marking badge. Colors use the solid CAPCO
 * standard tokens from Badge variants (saturated fill, white text, black text
 * for TOP SECRET). Use everywhere a classification level is rendered.
 */
export const ClassificationBadge: FC<ClassificationBadgeProps> = (props) => {
  const { classification, className, size = 'xs' } = props;
  return (
    <Badge
      className={className}
      size={size}
      variant={VARIANT[classification]}
    >
      {LABEL[classification]}
    </Badge>
  );
};
